/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { AppWindowIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { SessionViewModel } from "../../features/session/session-view-model"
import { useViewModel } from "../app/app-hooks"
import { ApplicationSearch } from "../components/application-search"
import { Card } from "../primitives/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../primitives/table"
import { DashboardRow } from "./dashboard-row"
import { DashboardTile } from "./dashboard-tile"

/**
 * Keeps count columns as narrow as their headers allow, leaving the remaining
 * width for the application name.
 *
 * The right padding reserves space for the count link's arrow, keeping each
 * header aligned with its number. `last:pe-8` overrides `TableHead`'s default
 * last-column padding. In the narrow layout, the arrow is always visible, so
 * the header uses the cell's normal right padding instead.
 */
const COUNT_HEAD_CLASSES =
    "w-0 pe-8 text-right last:pe-8 @max-xl/dashboard:px-2 @max-xl/dashboard:text-xs @max-xl/dashboard:last:pe-2"

/**
 * Summarizes Insecure, Outdated, and Active device counts across enabled
 * applications and lists the counts for each application.
 *
 * Insecure and Active counts link to the application's device list. When no
 * applications are enabled, the table is replaced by an empty state and the
 * summary tiles show zero.
 */
export const DashboardView = observer(() => {
    const vm = useViewModel(ApplicationTypeViewModel)
    const sessionVM = useViewModel(SessionViewModel)

    const appTypes = vm.filterFavApplicationTypes(sessionVM.enabledApps)
    const isLoading = appTypes.some(({ id }) => !vm.threatOverviewFor(id ?? null))

    // biome-ignore lint/correctness/useExhaustiveDependencies: `enabledApps` returns a new array on every read, so the effect keys on its joined IDs
    useEffect(() => {
        vm.recalculateAggregatedThreats(sessionVM.enabledApps)
    }, [sessionVM.enabledApps.join(",")])

    return (
        <div className="w-content">
            {/*
             * The `dashboard` container switches to the narrow layout below `@xl`, where
             * the wide row leaves no room for the application name. It measures the page,
             * not the window.
             */}
            <div className="@container/dashboard max-w-5xl">
                <header className="mb-6 flex flex-wrap items-start justify-between gap-y-4">
                    <h1 className="m-0 text-3xl font-bold text-primary">Dashboard</h1>
                    <ApplicationSearch />
                </header>

                <section
                    aria-label="Totals across enabled applications"
                    className="mb-6 grid gap-2 @lg/dashboard:grid-cols-3 @lg/dashboard:gap-6"
                >
                    <DashboardTile kind="insecure" count={vm.aggregatedInsecure} />
                    <DashboardTile kind="outdated" count={vm.aggregatedOutdated} />
                    <DashboardTile kind="active" count={vm.aggregatedActive} />
                </section>

                {appTypes.length > 0 ? (
                    <Card className="py-0">
                        {/* Drop the row hover. Only the count cells are clickable, and they tint themselves. */}
                        <Table
                            aria-label="Enabled applications"
                            aria-busy={isLoading}
                            className="[&_tr]:hover:bg-transparent"
                        >
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="@max-xl/dashboard:text-xs">Application Name</TableHead>
                                    <TableHead className={COUNT_HEAD_CLASSES}>Insecure</TableHead>
                                    <TableHead className={COUNT_HEAD_CLASSES}>Outdated</TableHead>
                                    <TableHead className={COUNT_HEAD_CLASSES}>Active</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {appTypes.map((appType) => (
                                    <DashboardRow
                                        key={appType.id}
                                        appType={appType}
                                        threat={vm.threatOverviewFor(appType.id ?? null)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                ) : (
                    renderEmpty(vm.hasUserAppTypes)
                )}
            </div>
        </div>
    )
})

/**
 * Renders the empty state when no application is enabled. A user with no
 * application assigned has nothing to enable, so the copy sends them to an
 * administrator instead.
 */
function renderEmpty(hasAssignedApps: boolean) {
    const { title, description } = hasAssignedApps
        ? {
              title: "No applications enabled",
              description: "Open Applications in the sidebar and turn one on."
          }
        : {
              title: "No applications assigned",
              description: "Ask your administrator to assign one."
          }

    return (
        <Empty className="border bg-background">
            <EmptyHeader>
                <EmptyMedia>
                    <AppWindowIcon />
                </EmptyMedia>
                <EmptyTitle>{title}</EmptyTitle>
                <EmptyDescription>{description}</EmptyDescription>
            </EmptyHeader>
        </Empty>
    )
}
